import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import {
  createUUID,
  filterInactiveAttributes,
  getSelectedAttributeValues,
  sortCategoryAttributes,
} from "@miplatform/utils";

export const useSpecMaterialFormViewModel = ({
  stateManager,
  planDetails,
  handleAddItems = () => {},
}) => {
  // PlanArea states
  const [checkedAreas, setCheckedAreas] = useState([]);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  // item category drawer state
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [filteredMaterialOptions, setFilteredMaterialOptions] = useState([]);
  const [categoryFilterOptions, setCategoryFilterOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [selectedAttributeValues, setSelectedAttributeValues] = useState({});
  const [search, setSearch] = useState("");
  const [openMaterialResults, setOpenMaterialResults] = useState(false);
  const [isSuppliedByBuilder, setIsSuppliedByBuilder] = useState(false);

  // item templates drawer states
  const [itemTemplatesResult, setItemTemplatesResult] = useState([]);
  const [openItemTemplateResults, setOpenItemTemplateResults] = useState(false);
  const [selectedItemTemplate, setSelectedItemTemplate] = useState([]);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [areaTypeOptions, setAreaTypeOptions] = useState([]);
  const [itemTemplateSearch, setItemTemplateSearch] = useState("");
  const [filteredAreaTypeOptions, setFilteredAreaTypeOptions] = useState([]);
  const [templateFilter, setTemplateFilter] = useState({
    seriesId: planDetails?.seriesId,
    areaTypeId: [],
    builderId: planDetails?.builderId,
  });

  const debouncedSearch = useDebounce(search, 300);
  const debouncedItemTemplateSearch = useDebounce(itemTemplateSearch, 300);
  const hasFilters = !!selectedCategory?.categoryId || !!debouncedSearch;
  const materialResults =
    Object.keys(selectedAttributeValues).length > 0
      ? filteredMaterialOptions
      : materialOptions;
  const noResultsHelperText = hasFilters
    ? "Please adjust your search or filter criteria and try again."
    : "Select a category or search for an item.";

  const GET_MATERIAL_OPTIONS_UUID = createUUID();
  const GET_CATEGORY_FILTERS_UUID = createUUID();
  const GET_SERIES_OPTIONS_UUID = createUUID();
  const GET_AREA_TYPE_OPTIONS_UUID = createUUID();
  const GET_ITEM_TEMPLATES_UUID = createUUID();

  // Category Filter API methods
  const getMaterialOptions = () => {
    const filters = {
      or: [
        { category: { itemCode: { contains: debouncedSearch } } },
        { category: { itemDescription: { contains: debouncedSearch } } },
      ],
      ...(selectedCategory?.categoryId && {
        category: {
          categoryId: { type: "guid", value: selectedCategory.categoryId },
        },
      }),
    };

    stateManager.abort(GET_MATERIAL_OPTIONS_UUID);
    stateManager.run(async () => {
      const response = await homeService.materials.getAll({
        sort: { category: "itemCode" },
        filters,
      });

      if (response.status === HTTP_STATUSES.OK) {
        const filteredData = filterInactiveAttributes(response.data).map(
          (material) => {
            return {
              ...material,
              materialId: material.id,
            };
          }
        );
        setMaterialOptions(filteredData);
      }
    }, GET_MATERIAL_OPTIONS_UUID);
  };

  const getCategoryFilters = () => {
    stateManager.abort(GET_CATEGORY_FILTERS_UUID);
    stateManager.run(async () => {
      const response = await homeService.categories.getFilters();
      if (response.status === HTTP_STATUSES.OK) {
        setCategoryFilterOptions(response.data);
      }
    }, GET_CATEGORY_FILTERS_UUID);
  };

  // Item Tempalte Filter API methods
  const getSeriesOptions = () => {
    stateManager.abort(GET_SERIES_OPTIONS_UUID);
    stateManager.run(async () => {
      const response = await homeService.plans.getAll({
        sort: "name",
        filters: {
          builderId: { type: "guid", value: planDetails?.builderId },
          isItemTemplate: true,
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setSeriesOptions(response.data);
      }
    }, GET_SERIES_OPTIONS_UUID);
  };

  const getAreaTypeOptions = () => {
    stateManager.abort(GET_AREA_TYPE_OPTIONS_UUID);
    stateManager.run(async () => {
      const response = await homeService.areaTypes.getAll({ sort: "name" });
      if (response.status === HTTP_STATUSES.OK) {
        setAreaTypeOptions(response.data);
      }
    }, GET_AREA_TYPE_OPTIONS_UUID);
  };

  const getItemTemplates = () => {
    const { seriesId, areaTypeId } = templateFilter;
    const filters = [
      {
        builderId: { type: "guid", value: planDetails.builderId },
        planAreaLabel: { contains: debouncedItemTemplateSearch },
        ...(seriesId && { seriesId: { type: "guid", value: seriesId } }),
        ...(areaTypeId.length > 0 && { areaTypeId: { in: areaTypeId } }),
      },
    ];
    stateManager.abort(GET_ITEM_TEMPLATES_UUID);
    stateManager.run(async () => {
      const response = await homeService.plans.itemTemplates.getAll({
        filters,
        sort: "planAreaLabel",
      });
      if (response.status !== HTTP_STATUSES.OK) {
        return;
      }
      if (areaTypeId.length === 0) {
        const filteredAreaTypeOptions = areaTypeOptions?.filter((item) =>
          response.data.some((d) => d.areaTypeId === item.id)
        );
        setFilteredAreaTypeOptions(filteredAreaTypeOptions);
      }
      setItemTemplatesResult(response.data);
    }, GET_ITEM_TEMPLATES_UUID);
  };

  // add/remove items to areas
  const handleCheckArea = (area, checked) => {
    if (checked) {
      setCheckedAreas((prev) => [...prev, area]);
    } else {
      setCheckedAreas((prev) => prev.filter((a) => a.id !== area.id));
    }
  };

  const updateMaterialsInArea = (area, materialsToAdd) => {
    const existingMaterials = area.materials;
    const updatedMaterials = [...existingMaterials];
    materialsToAdd.forEach((item) => {
      const isMaterialAlreadyAdded = existingMaterials.some(
        (material) => material.materialId === item.id
      );
      if (!isMaterialAlreadyAdded) {
        updatedMaterials.push({
          materialId: item.materialId || item.id,
          suppliedByBuilder: isSuppliedByBuilder || item.suppliedByBuilder,
          quantity: item.quantity,
        });
      }
    });
    return { ...area, materials: updatedMaterials };
  };

  // Category search/filter handlers
  const handleResetItemSelection = () => {
    setCheckedAreas([]);
    setSelectedItems([]);
    setIsSuppliedByBuilder(false);
  };

  const handleResetFilter = () => {
    setSelectedCategory({});
    setSelectedAttributeValues({});
  };

  const handleResetFilterSelection = () => {
    setSelectedAttributeValues({});
    setSearch("");
  };

  const handleResetMaterialOptions = () => {
    setMaterialOptions([]);
    setFilteredMaterialOptions([]);
    setSelectedItems([]);
  };

  const handleResetAddItems = () => {
    // reset everything
    handleResetFilter();
    handleResetFilterSelection();
    handleResetMaterialOptions();
    handleResetItemSelection();
  };

  const handleCategoryClick = (category) => {
    const sortedCategory = sortCategoryAttributes(category);
    setSelectedCategory(sortedCategory);
    setOpenMaterialResults(true);
    setSelectedItems([]);
  };

  const handleAttributeValueClick = (attributeId, attributeValue) => {
    setOpenMaterialResults(true);
    getSelectedAttributeValues(
      attributeId,
      attributeValue,
      selectedAttributeValues,
      setSelectedAttributeValues
    );
    setSelectedItems([]);
  };

  const handleItemSearch = (value) => {
    setSearch(value);
    setOpenMaterialResults(true);
  };

  const handleAddItemToAreas = () => {
    const updatedAreas = checkedAreas.map((area) =>
      updateMaterialsInArea(area, [selectedItems])
    );
    handleAddItems(updatedAreas);
    handleResetItemSelection();
  };

  // Item Template handlers
  const handleSeriesOptionFilter = (seriesId) => {
    const isSelected = templateFilter.seriesId === seriesId;
    setOpenItemTemplateResults(true);
    if (isSelected) {
      setTemplateFilter((prev) => ({
        ...prev,
        seriesId: "",
        areaTypeId: [],
      }));
    } else {
      setTemplateFilter((prev) => ({
        ...prev,
        seriesId,
        areaTypeId: [],
      }));
    }
  };

  const handleAreaTypeOptionFilter = (item) => {
    const isSelected = templateFilter.areaTypeId?.includes(item.id);
    setOpenItemTemplateResults(true);
    if (isSelected) {
      setTemplateFilter((prev) => ({
        ...prev,
        areaTypeId: prev.areaTypeId.filter((id) => id !== item.id),
      }));
    } else {
      setTemplateFilter((prev) => ({
        ...prev,
        areaTypeId: [...prev.areaTypeId, item.id],
      }));
    }
  };

  const handleCloseItemTemplateResults = () => {
    setItemTemplateSearch("");
    setOpenItemTemplateResults(false);
    setTemplateFilter({
      seriesId: "",
      areaTypeId: [],
      builderId: planDetails?.builderId,
    });
  };

  const handleItemTemplateSearch = (value) => {
    setItemTemplateSearch(value);
    setOpenItemTemplateResults(true);
  };

  const resetMaterialDrawerStates = () => {
    setItemTemplateSearch("");
    setOpenMaterialResults(false);
    setOpenItemTemplateResults(false);
    setTemplateFilter({
      seriesId: planDetails?.seriesId,
      areaTypeId: [],
      builderId: planDetails?.builderId,
    });
    handleResetFilter();
    handleResetFilterSelection();
    handleResetMaterialOptions();
  };

  const handleAddItemTemplatesToAreas = () => {
    const updatedAreas = checkedAreas.map((area) =>
      updateMaterialsInArea(area, selectedItems)
    );
    handleAddItems(updatedAreas);
    setCheckedAreas([]);
  };

  return {
    selectedItems,
    checkedAreas,
    isSuppliedByBuilder,
    materialOptions,
    filteredMaterialOptions,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    selectedPlaceholder,
    search,
    showCategoryFilter,
    itemTemplatesResult,
    openItemTemplateResults,
    selectedItemTemplate,
    openMaterialResults,
    debouncedSearch,
    debouncedItemTemplateSearch,
    hasFilters,
    isLoading: stateManager.isBusy(),
    materialResults,
    noResultsHelperText,
    templateFilter,
    itemTemplateSearch,
    seriesOptions,
    filteredAreaTypeOptions,
    setTemplateFilter,
    getAreaTypeOptions,
    getSeriesOptions,
    getItemTemplates,
    handleSeriesOptionFilter,
    handleAreaTypeOptionFilter,
    resetMaterialDrawerStates,
    setOpenMaterialResults,
    setItemTemplatesResult,
    setOpenItemTemplateResults,
    setSelectedItemTemplate,
    setShowCategoryFilter,
    setSelectedItems,
    setCheckedAreas,
    setIsSuppliedByBuilder,
    setMaterialOptions,
    setFilteredMaterialOptions,
    setSelectedPlaceholder,
    getCategoryFilters,
    getMaterialOptions,
    handleCheckArea,
    handleAddItemToAreas,
    handleResetItemSelection,
    handleResetFilter,
    handleResetAddItems,
    handleCategoryClick,
    handleAttributeValueClick,
    handleAddItemTemplatesToAreas,
    handleResetMaterialOptions,
    handleCloseItemTemplateResults,
    handleItemSearch,
    handleItemTemplateSearch,
    setCategoryFilterOptions,
    handleResetFilterSelection,
  };
};
